/**
 * Domain types, read off `tbs-planhub-api`'s SOURCE (`src/routes/*`, `src/services/*`) and its
 * `db/001`–`008` migrations. Reconciled against the running contract on 2026-08-16 — see
 * README § "API contract: assumed vs actual" for what changed.
 *
 * 🔴 EVERY ID IS A STRING, NOT A NUMBER. The API is Postgres + node-postgres with no
 * `setTypeParser` override, so BIGINT and NUMERIC arrive as strings — `{"id":"1042"}`. Typing them
 * as `number` would have compiled and worked in every test with a small id, then broken silently
 * past 2^53 or, sooner, made `ticket.id === selectedId` false because "1042" !== 1042.
 */

export type Id = string;

// ── Vocabularies — GET /api/v1/settings/{collection} → { items: [...] } ───────────────────────────

export interface LaneDef {
  code: string;
  label: string;
  sort_order: number;
  colour: string | null;
  is_active: boolean;
}

export interface StatusDef {
  code: string;
  label: string;
  lane: string;
  sort_order: number;
  is_initial: boolean;
  is_final: boolean;
  colour: string | null;
  is_active: boolean;
}

export interface TypeDef {
  code: string;
  label: string;
  sort_order: number;
  icon: string | null;
  is_subtask: boolean;
  is_active: boolean;
}

export interface PriorityDef {
  code: string;
  label: string;
  sort_order: number;
  colour: string | null;
  is_active: boolean;
}

export interface ImpactDef {
  code: string;
  label: string;
  sort_order: number;
  is_active: boolean;
}

/**
 * `GET /settings/resolutions` — a SIXTH collection this app did not fetch.
 *
 * ⚠️ IT IS NOT JIRA'S RESOLUTION AND MUST NOT BE "CORRECTED" INTO ONE. db/007 says it in as many
 * words: Jira answers "done HOW — Fixed / Won't Do / Duplicate"; Or's values answer "DELIVERED how —
 * Hotfix / Bugfix / By Sprint / Immediate Fix / Database Fix". A delivery route, not an outcome.
 *
 * ⭐ Kept OUT of `Vocabularies` deliberately: that object is fetched on every authenticated screen,
 * and only the List view has a Resolution column. Adding a sixth request to every page to serve one
 * of them is a cost the other pages have no reason to pay.
 */
export interface ResolutionDef {
  code: string;
  label: string;
  sort_order: number;
  is_active: boolean;
}

export interface Vocabularies {
  statuses: StatusDef[];
  lanes: LaneDef[];
  types: TypeDef[];
  priorities: PriorityDef[];
  impacts: ImpactDef[];
}

// ── Identity — GET /auth/me → { user: SafeUser } ──────────────────────────────────────────────────

/**
 * 🔴 THREE VALUES, NOT TWO. `'service'` has existed since the API's db/008 and was missing from both
 * sides of the wire — the API's own `SafeUser` type omitted it too, because `resolveServiceAccount`
 * CASTS its row and a cast is where the compiler stops helping.
 *
 * ⭐ It matters here rather than being pedantry: the admin user list RENDERS this field, and a union
 * that cannot express `'service'` would have forced a cast or an `undefined` label on exactly the
 * rows an admin is looking at the table to find — the non-human ones.
 */
export type AuthMethod = 'gitlab' | 'local' | 'service';

/** Mirrors `SafeUser` in the API's `src/db/users.ts`. ⛔ It has no avatar URL; we render initials. */
export interface CurrentUser {
  id: Id;
  email: string;
  display_name: string;
  auth_method: AuthMethod;
  is_active: boolean;
  is_admin: boolean;
  gitlab_user_id: Id | null;
  gitlab_username: string | null;
  user_type: 'human' | 'ai_agent';
}

// ── Local login — POST /auth/local/* ──────────────────────────────────────────────────────────────

/**
 * 🔴 WHAT A CORRECT PASSWORD RETURNS. Not a session — a half-authenticated state that opens nothing.
 *
 * ⭐⭐ The `status` discriminator decides which screen comes next, and the two cases are genuinely
 * different journeys rather than one with a flag: `totp_required` asks for six digits, while
 * `totp_enrolment_required` has to show a QR, get it scanned, and only then ask. Modelling them as
 * one "needs 2FA" boolean is how the enrolment case ends up rendering a code box for a secret the
 * user has not been given yet.
 */
export interface LoginChallenge {
  status: 'totp_required' | 'totp_enrolment_required';
  /**
   * ⛔ HELD IN MEMORY ONLY — never localStorage, never sessionStorage, never a cookie this app sets.
   * It is a partial credential: combined with a phone it becomes a session. Persisting it would
   * survive a tab close and leave half a login on the disk of a shared machine.
   */
  challenge: string;
  expires_at: string;
}

/** `POST /auth/local/totp/enrol` — the QR payload for an enrolment in progress. */
export interface EnrolmentOffer {
  otpauth_uri: string;
  /** ⭐ The base32, for the "can't scan it" path. It is the user's own secret and they must be able
   *  to type it — a QR-only enrolment is unusable when the browser IS the phone. */
  secret: string;
}

// ── Admin — GET /api/v1/admin/users ───────────────────────────────────────────────────────────────

/**
 * One row of the admin user table.
 *
 * ⭐ `auth_method` AND `user_type` are both here because they answer different questions:
 * `user_type` separates a PERSON from an AGENT, `auth_method` says how that principal proves who it
 * is. An admin asking "which of these are bots" and one asking "who can log in with a password" are
 * not asking the same thing, and one column cannot answer both.
 * ⛔ There is no secret on this type and none on the wire — enrolment arrives as a BOOLEAN the API
 * computes in SQL.
 */
export interface AdminUser {
  id: Id;
  email: string;
  display_name: string;
  auth_method: AuthMethod;
  user_type: 'human' | 'ai_agent';
  is_active: boolean;
  is_admin: boolean;
  totp_enrolled: boolean;
  /** A count, as a string — it is a Postgres bigint. */
  active_sessions: string;
  created_at: string;
  last_login_at: string | null;
}

export interface IssuedInvite {
  email: string;
  expires_at: string;
  /** ⭐ Shown ONCE. The API cannot return it again; there is no mailer, so it is handed over by hand. */
  invite_token: string;
  note: string;
}

export interface IssuedReset {
  reset_token: string;
  expires_at: string;
  note: string;
}

export interface AuditEntry {
  id: Id;
  actor_id: Id;
  actor_email: string;
  action: string;
  target_type: string;
  target_id: string;
  detail: Record<string, unknown> | null;
  created_at: string;
}

/**
 * 🔴 The handle that `ticket.assignee` and `ticket.reporter` actually contain.
 * Copied from the API's own `userHandle()` — if these two ever disagree, "Only mine" silently
 * returns nothing and looks like an empty backlog rather than a bug.
 */
export function userHandle(user: CurrentUser): string {
  return user.gitlab_username ?? user.email;
}

// ── Project — GET /api/v1/projects → { items: [...] } ─────────────────────────────────────────────

export interface Project {
  id: Id;
  key: string;
  name: string;
  /** 'software' | 'research' — decides which status set the project uses. */
  kind?: string;
  gitlab_project_id: Id | null;
}

// ── Sprints — GET /api/v1/projects/:id/sprints → { items: [...] } ────────────────────────────────

/**
 * One option in the sprint filter.
 *
 * ⭐ The API returns these ALREADY ORDERED — active, then planned soonest-first, then closed
 * most-recent-first. ⛔ This client must not re-sort them: the ordering encodes "which sprint is
 * anybody actually filtering by today", which the client cannot recompute without knowing that
 * db/001 permits exactly one active sprint per project.
 *
 * ⚠️ `starts_on`/`ends_on` are `YYYY-MM-DD`, cast to text server-side ON PURPOSE. A Postgres DATE
 * read through node-postgres arrives as a JS Date at LOCAL midnight, which JSON-serialises to the
 * PREVIOUS DAY in Asia/Bangkok. Do not `new Date()` these and print the date part.
 */
export interface Sprint {
  id: Id;
  name: string;
  /** What the sprint is FOR — db/001's own column. */
  goal: string | null;
  state: 'active' | 'planned' | 'closed';
  starts_on: string;
  ends_on: string;
  version_id: Id | null;
  version_name: string | null;
  /** `(26 work items)`. ⚠️ Sub-tasks INCLUDED — the API's type says so explicitly. */
  ticket_count: number;
  /** `43.5`. ⭐ A NUMBER, not a NUMERIC string — the API casts `::float8` so `a + b` cannot concatenate. */
  story_points_total: number;

  /**
   * ── PH-37: the done / in-progress / not-started split ─────────────────────────────────────────
   *
   * Or: *"did planhub UI have sum of story point on-progress and done so we know what left in each
   * sprint"*. These seven fields are that answer, and they come from the API's SINGLE lateral
   * aggregate — the same one `story_points_total` rides on — so a COLLAPSED sprint header still
   * costs no request. See the API's `src/services/sprint-progress.ts` for what each bucket means.
   *
   * 🔴 THE BUCKETS ARE NOT RECOMPUTED IN THIS CLIENT, AND THAT IS THE POINT. "Done" is
   * `status_def.is_final`, which is ADMIN-EDITABLE; "not started" is the lane of the project's own
   * `initial_status`, which this client is never told. ⇒ Any client-side rule would have to hard-code
   * a status list or the lane `'intake'`, and both go stale SILENTLY the first time an admin changes
   * the vocabulary — the header would keep rendering, just wrong. ⛔ One definition, in SQL, next to
   * the schema that owns the flags.
   *
   * ⚠️ ALL SEVEN ARE OPTIONAL, deliberately, and `sprint-progress.ts` refuses to draw a split unless
   *   every one of them arrived AND they add up. A web build deployed against an API from before
   *   PH-37 must show the old header, not `NaN` and not a bar reading 0% done — the same "degrade
   *   visibly" rule `modules`/`fix_versions` follow on `TicketListItem`.
   */
  story_points_done?: number;
  story_points_in_progress?: number;
  story_points_not_started?: number;
  ticket_count_done?: number;
  ticket_count_in_progress?: number;
  ticket_count_not_started?: number;
  /**
   * Tickets that are NOT in a final status and carry no estimate.
   * ⭐ The qualifier on "points left": that number cannot see these, and this one says how many it
   *   cannot see. ⛔ Without it "0 pts left" over three unestimated open tickets reads as "finished".
   */
  unestimated_remaining_count?: number;
}

// ── Tickets ───────────────────────────────────────────────────────────────────────────────────────

/**
 * One row of `GET /api/v1/tickets`. `key` is DERIVED in SQL (`project.key || '-' || number`) and is
 * always present, so the client never assembles it.
 *
 * ⚠️ THIS NOTE USED TO SAY `modules` AND `fix_versions` ARE NOT ON THIS RESPONSE. THEY ARE — read
 * off `TICKET_SELECT` in the API's `src/services/tickets.ts`, which `listTickets` uses unchanged and
 * the route returns unwrapped. The claim was true when written and went stale at `680ab8b`; the
 * `secondLineIncomplete` notice in BacklogPage.tsx is therefore a guard that no longer fires rather
 * than a description of today. ⛔ Both are still OPTIONAL here on purpose — the row degrades visibly
 * if a deployment ever stops sending them instead of inventing "None" for every ticket.
 */
export interface TicketListItem {
  id: Id;
  project_id: Id;
  number: string;
  key: string;
  title: string;
  description?: string | null;
  type: string;
  status: string;
  priority: string;
  impact: string | null;
  resolution: string | null;
  /** NUMERIC(5,2) — a string over the wire. */
  story_points: string | null;
  assignee: string | null;
  reporter: string;
  sprint_id: Id | null;
  /**
   * ⭐ The NAME, alongside the id — the API has sent both since the beginning (`s.name AS
   * sprint_name` in `TICKET_SELECT`), and this client simply never declared it. That omission is why
   * the row could not show a sprint: not a missing endpoint, a field nobody read.
   *
   * ⚠️ OPTIONAL, AND THE ABSENT/NULL DISTINCTION IS DELIBERATELY NOT DRAWN HERE — unlike `modules`,
   * where it is. The row's answer is identical either way (no chip), so surfacing "the API forgot"
   * would be a notice a reader can do nothing with. `modules` earns the distinction because "this
   * ticket has none" is a fact worth printing as `None`; a sprint has no such rendering.
   */
  sprint_name?: string | null;
  version_id: Id | null;
  parent_id: Id | null;
  start_date: string | null;
  end_date: string | null;
  gitlab_issue_iid: Id | null;
  created_at: string;
  updated_at: string;
  modules?: string[];
  /**
   * ⚠️ TWO SHAPES. The API sends `text[]` of NAMES (`array_agg(fver.name)`, added at `680ab8b`); this
   * client was written against an earlier guess of `{id, name}[]`. Both are accepted and are read
   * through `versionNames()` — ⛔ never `.map(v => v.name)`, which prints `undefined` for a string.
   */
  fix_versions?: (string | { id: Id; name: string })[];
}

export interface TicketComment {
  id: Id;
  ticket_id: Id;
  author: string;
  body: string;
  created_at: string;
  updated_at: string;
}

/** db/007 — links only, no uploads. `kind='image'` goes in an <img>; nothing else ever does. */
export interface TicketLink {
  id: Id;
  ticket_id: Id | null;
  comment_id: Id | null;
  kind: 'image' | 'drive' | 'kg' | 'url';
  url: string;
  title: string | null;
  created_at: string;
}

export interface StatusHistoryEntry {
  id: Id;
  /** NULL only for the very first row — db/001. */
  from_status: string | null;
  to_status: string;
  changed_by: string;
  changed_at: string;
  note: string | null;
}

/**
 * Assembled client-side from four calls, because the API splits them:
 *   GET /tickets/:id            → { ticket, modules, history }
 *   GET /tickets/:id/comments   → { items }
 *   GET /tickets/:id/links      → { items }
 *   GET /tickets?parentId=:id   → { items }   (sub-tasks)
 */
export interface TicketDetail extends TicketListItem {
  comments: TicketComment[];
  links: TicketLink[];
  subtasks: TicketListItem[];
  status_history: StatusHistoryEntry[];
}

/**
 * One legal target from `GET /tickets/:id/transitions` → `{ ticket_id, current_status, allowed }`,
 * where each `allowed` row is `{ code, label, lane, colour, note }`.
 */
export interface Transition {
  to_status: string;
  label: string | null;
  colour: string | null;
  note: string | null;
}

export interface Page<T> {
  items: T[];
  total: number;
  /** Derived from the API's offset/limit — it paginates by offset, the UI reads by page. */
  page: number;
  per_page: number;
}

/**
 * The orderings the API offers. Read off its `TICKET_ORDERINGS` in `src/services/tickets.ts` and the
 * zod schema `sort: z.enum(['updated','rank','created','priority','status','key','points'])`:
 *
 *   updated  → `t.updated_at DESC, t.id DESC`            (the endpoint's default)
 *   rank     → `t.backlog_rank ASC NULLS LAST, t.id ASC`
 *   created  → `t.created_at`
 *   priority → `priority_def.sort_order`  ⭐ by MEANING, not the code's alphabet
 *   status   → `status_def.sort_order`    ⭐ the board's left-to-right order
 *   key      → `p.key, t.number`          ⭐ NUMERIC, so TBS-9 precedes TBS-10
 *   points   → `t.story_points NULLS LAST`
 *
 * ⚠️ THIS COMMENT USED TO SAY "THE ONLY TWO ORDERINGS THAT EXIST", and that every other column would
 * have to be re-sorted in the browser. That is no longer true and the reasoning must not outlive the
 * constraint: sorting is still done SERVER-SIDE for every one of these, so page 2 continues to be
 * ordered by the same rule as page 1. Nothing here re-sorts a page it happens to hold.
 */
export type TicketSort =
  | 'updated'
  | 'rank'
  | 'created'
  | 'priority'
  | 'status'
  | 'key'
  | 'points';

/**
 * ⛔ OMITTED IS NOT `desc`. The API has no endpoint-wide default direction, because `updated` is
 * descending and `rank` ascending; absent means "this ordering's own direction". Sending `dir=desc`
 * with `sort=rank` would silently invert the backlog.
 */
export type TicketSortDir = 'asc' | 'desc';

export interface TicketQuery {
  projectId?: Id | null;
  status?: string | null;
  assignee?: string | null;
  sprintId?: Id | null;
  /**
   * Tickets with NO sprint.
   *
   * ⭐ The API filter that replaced the backlog's page-scan. ⛔ Mutually exclusive with `sprintId` —
   * the API answers the combination with a 400 rather than an empty page, so do not send both.
   * ⚠️ `false` must be sent as omitted, not as the string "false" — see `buildUrl`.
   */
  noSprint?: boolean | null;
  /** Omitted means the API's own default, `updated` — an unchanged request keeps its old answer. */
  sort?: TicketSort | null;
  /** Omitted means the ordering's own direction. */
  dir?: TicketSortDir | null;
  page: number;
  per_page: number;
}
